import { Copy } from '@/components/icons'
import { Share } from '@/components/icons/Share'
import { eventTracker } from '@/config/trackingFunctions'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useUserDetails } from '@/hooks/useUserDetails'
import { Popover } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import {
  EmailIcon,
  EmailShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share'
import { Tooltip } from 'react-tooltip'
import { useCopyToClipboard } from 'usehooks-ts'
import { type ShareTypeName, SHARE_TYPES } from './shareTypes'
import { type TrackingEventName } from '@/constants/TrackingConstants'

type Props = {
  className?: string
  iconClass?: string
  shareText: string
  shareUrl: string
  pageName: TrackingEventName
  shareContent: TrackingEventName
}

export const ShareButton = ({ className, iconClass, shareText, shareUrl, pageName, shareContent }: Props) => {
  const { t } = useTranslation()

  const [value, copy] = useCopyToClipboard()

  const userData = useUserDetails()

  const trackEvent = (sharePlatform: ShareTypeName) => {
    eventTracker(TRACKING_TYPES.SHARE_DONE, {
      ...userData,
      'Page Name': pageName,
      'Share Platform': sharePlatform,
      'Share Content Type': shareContent,
    })
  }

  return (
    <Popover className="relative">
      <Popover.Button
        className={`flex items-center justify-center py-2 px-4 rounded-full gap-x-1 text-sm font-semibold bg-opacity-10 hover:bg-opacity-30 bg-white text-white ${className}`}
      >
        <Share className={`text-white ${iconClass}`} />
        {t('common.share')}
      </Popover.Button>
      <Popover.Panel className="absolute z-10">
        <div className="bg-white rounded flex gap-x-2 p-2 mt-2 shadow-md shadow-slate-400">
          <TwitterShareButton title={shareText} url={`\n${shareUrl}`} onClick={() => trackEvent(SHARE_TYPES.TWITTER)}>
            <TwitterIcon size={28} round />
          </TwitterShareButton>

          <WhatsappShareButton title={shareText} url={`\n${shareUrl}`} onClick={() => trackEvent(SHARE_TYPES.WHATSAPP)}>
            <WhatsappIcon size={28} round />
          </WhatsappShareButton>

          <EmailShareButton body={shareText} url={`\n${shareUrl}`} onClick={() => trackEvent(SHARE_TYPES.EMAIL)}>
            <EmailIcon size={28} round />
          </EmailShareButton>

          <button
            className="border p-1 h-7 w-7 rounded-full flex items-center justify-center my-anchor-element"
            onClick={() => {
              copy(`${shareText} \n${shareUrl}`)
              trackEvent(SHARE_TYPES.LINK)
            }}
          >
            <Copy className="text-primary-500 w-4 h-4" />
            <Tooltip anchorSelect=".my-anchor-element" place="bottom">
              {value ? 'Link Copied!' : 'Copy to clipboard'}
            </Tooltip>
          </button>
        </div>
      </Popover.Panel>
    </Popover>
  )
}
